<template>
  <q-page class="q-pa-md">
    <div
      v-if="headerTitle"
      class="text-h6 text-dark q-mb-sm"
      v-html="headerTitle"
    ></div>

    <div v-if="!selectedParentId" class="row q-col-gutter-sm">
      <div
        v-for="category in parentCategories"
        :key="category._id"
        :class="category.name === 'All Brand' ? 'col-12' : 'col-6'"
      >
        <q-card
          flat
          bordered
          class="q-pa-xs text-center full-height row items-center justify-center cursor-pointer"
          style="border: 1px solid var(--q-primary); min-height: 40px"
          @click="handleClick(category)"
        >
          <q-card-section class="row q-pa-none full-width">
            <template v-if="category.name !== 'All Brand'">
              <div
                class="text-dark wee-text-weight-bold"
                :class="`text-${category?.color}`"
                v-fit-text="{ max: 14, min: 12 }"
              >
                {{ category?.name }}
              </div>
              <div
                class="text-dark wee-text-weight-bold"
                :class="`text-${category?.color}`"
                v-fit-text="{ max: 14, min: 12 }"
              >
                {{ category?.description }}
              </div>
            </template>
            <template v-else>
              <div
                class="text-dark wee-text-weight-bold"
                :class="`text-${category?.color}`"
                v-fit-text="{ max: 14, min: 12 }"
              >
                {{ category?.name + "  " + category?.description }}
              </div>
            </template>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <div v-else class="row q-col-gutter-sm">
      <div
        v-for="category in childCategories"
        :key="category._id"
        class="col-6"
      >
        <q-card
          flat
          bordered
          class="q-pa-none text-center full-height row justify-center items-center cursor-pointer category-card"
          style="border: 1px solid var(--q-primary); min-height: 50px"
          @click="handleClick(category)"
        >
          <q-img
            :src="getImageFullPath(category?.image) || ImageDefault"
            ratio="1"
            class="q-mb-sm"
            width="100%"
            height="75%"
            fit="cover"
            loading="lazy"
          />
          <q-card-section class="row q-pa-none full-width">
            <div
              class="text-dark wee-text-weight-bold"
              v-fit-text="{ max: 14, min: 12 }"
            >
              {{ category?.name }}
            </div>
            <div
              class="text-dark wee-text-weight-bold"
              v-fit-text="{ max: 14, min: 12 }"
            >
              {{ category?.description }}
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import type { ICategory } from "@/types/models";
import type { PropType } from "vue";
import { computed, ref, watch } from "vue";
import { ImageDefault } from "@/libs/constant";
import { NAIL_SUPPLY_ROUTE_NAME } from "@/libs/route-name";
import { removeHtmlTags, getImageFullPath } from "@/utils/appUtil";
import { useBase } from "@/composables/useBase";
import { useRoute } from "vue-router";

const props = defineProps({
  categories: {
    type: Array as PropType<ICategory[]>,
    required: true
  }
});

const { router } = useBase();
const route = useRoute();
const selectedParentId = ref<string | null>(null);

watch(
  () => route.query.parent_id,
  newParentId => {
    if (newParentId) {
      selectedParentId.value = newParentId.toString();
    } else {
      selectedParentId.value = null;
    }
  },
  { immediate: true }
);

const parentCategories = computed(() => {
  return props.categories.filter(cat => !cat.parent_id);
});

const childCategories = computed(() => {
  if (!selectedParentId.value) return [];
  return props.categories.filter(
    cat => cat.parent_id && cat.parent_id.toString() === selectedParentId.value
  );
});

const headerTitle = computed(() => {
  if (!selectedParentId.value) {
    return "";
  }

  const parentCategory = props.categories.find(
    cat => cat._id.toString() === selectedParentId.value
  );

  return removeHtmlTags(parentCategory?.name || "");
});

const handleClick = (category: ICategory) => {
  const id = category._id.toString();

  const hasChildren = props.categories.some(
    cat => cat.parent_id && cat.parent_id.toString() === id
  );

  if (hasChildren) {
    router.push({
      query: { parent_id: id, type: category.type }
    });
  } else {
    router.push({
      name: NAIL_SUPPLY_ROUTE_NAME.PRODUCT,
      params: { category: category.slug },
      query: { type: category.type, catId: category._id }
    });
  }
};

watch(
  () => route.query.type,
  newType => {
    if (newType === "promotion") {
      const promotionCategory =
        props.categories.find(cat => cat.is_on_sale) || null;
      if (promotionCategory) {
        handleClick(promotionCategory);
      } else {
        selectedParentId.value = null;
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped></style>
